import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import CurrentUserCompaniesSelect from '../../../../../../../../components/GlobalHeader/CurrentUserCompaniesSelect'
import { NodeContentProps, NodesById } from '../../types'
import { NormalizedResetVisitor } from '../../normalizedVisitors/NormalizedResetVisitor'
import { useCalculatedReport } from '../../../../CalculatedReportContext'
import { currentUserSortedCompaniesSelector } from '../../../../../../../../redux/session/currentUser/selectors'

// Laajennettu props-tyyppi, joka sisältää normalisoidun datan
interface ExtendedNodeContentProps extends NodeContentProps {
  nodesById: NodesById
}

const CompanyNodeContent: React.FC<ExtendedNodeContentProps> = ({ node, rules, onUpdateValue, nodesById }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data: reportData } = useCalculatedReport()
  const arg = reportData?.args?.find(a => a.type === 'company')
  const companies = useSelector(currentUserSortedCompaniesSelector)

  // Handler for selecting an argument instead of a dimension
  const handleSelectArgument = () => {
    if (!arg?.id) return
    setOpen(false)
    const updated = { value: undefined, argumentId: arg.id }
    // Set the node to use an argument instead of a dimension
    onUpdateValue(node.id, updated)

    const visitor = new NormalizedResetVisitor(
      rules,
      nodesById,
      { type: node.type, ...updated },
      // Syötetään onUpdateValue-callback, jotta visitor voi päivittää solmuja
      onUpdateValue
    )

    // Aloitetaan visitointi tästä solmusta
    visitor.reset(node.id)
  }

  // Check if this option is currently selected
  const isUsingArgument = Boolean(node.argumentId)
  // Display value logic - when using argument, show empty string to avoid dimension showing
  let displayValue = ''
  if (isUsingArgument) {
    displayValue = t('global:dynamic')
  } else if (node.value) {
    // Check if the selected company exists in user's accessible companies
    const selectedCompany = companies.find(c => c.id === node.value)
    if (selectedCompany) {
      displayValue = String(node.value)
    } else if (node.company?.name) {
      // Company exists in node but user doesn't have access - show company name with restriction indicator
      displayValue = `${node.company.name} (${t('global:noAccess')})`
    } else {
      // Fallback if no company name is stored
      displayValue = `${t('global:restrictedAccess')} (${node.value})`
    }
  } else {
    displayValue = ''
  }

  const onValuesChange = (value: string) => {
    // Heataan valittu yritys ja laitetaan se kategoriaan mukaan
    const company = companies.find(c => c.id === value)

    const updated = { value, company, argumentId: null }
    // Päivitetään ensin tämän solmun arvo
    onUpdateValue(node.id, updated)

    // Luodaan NormalizedResetVisitor, joka nollaa tarvittavat arvot
    const visitor = new NormalizedResetVisitor(
      rules,
      nodesById,
      { type: node.type, ...updated },
      // Syötetään onUpdateValue-callback, jotta visitor voi päivittää solmuja
      onUpdateValue
    )

    // Aloitetaan visitointi tästä solmusta
    visitor.reset(node.id)
  }

  // Check if current company is restricted (not in user's companies)
  const isRestrictedCompany = node.value && !companies.find(c => c.id === node.value)

  return (
    <>
      <CurrentUserCompaniesSelect
        value={displayValue}
        onChange={onValuesChange}
        open={open}
        onOpenChange={setOpen}
        style={{ width: 200 }}
        status={isRestrictedCompany ? 'warning' : undefined}
        data-cy={`company-select-${node.id}`}
        popupRender={menu => (
          <div>
            {menu}
            <Divider />
            <Button
              color={isUsingArgument ? 'primary' : 'default'}
              variant={isUsingArgument ? 'filled' : 'outlined'}
              block
              disabled={!arg?.id}
              onClick={handleSelectArgument}
              data-cy={`select-argument-${node.id}`}
            >
              {t('global:dynamic')}
            </Button>
          </div>
        )}
      />
    </>
  )
}

export default CompanyNodeContent
